import React, { useEffect, useRef, useState } from "react";

export type DropOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type DropFieldProps = {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: DropOption[];
  placeholder?: string;
  style?: React.CSSProperties;
  uppercase?: boolean;
  disabled?: boolean;
  error?: string;
  locked?: boolean; // <<--- NOVO
};

const DropField: React.FC<DropFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Selecione...",
  style = {},
  uppercase = false,
  disabled = false,
  error,
  locked = false, // <<--- NOVO
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isLocked = disabled || locked; // <<--- unifica estado bloqueado
  const current = options.find((o) => o.value === value) || null;

  const isUpper = (s: string) => (uppercase ? s.toUpperCase() : s);

  const inputContainerStyle: React.CSSProperties = {
    width: style.width ?? "100%",
    height: "40px",
    backgroundColor: isLocked ? "#F3F3F3" : "#FFFFFF",
    border: `1px solid ${error ? "#D32F2F" : "#8A8A8A"}`,
    borderRadius: "2px",
    boxSizing: "border-box",
    position: "relative",
    cursor: isLocked ? "not-allowed" : "pointer",
    ...style,
  };

  // Fecha ao clicar fora
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div
      style={{
        marginBottom: "0px",
        marginLeft: style.marginLeft ?? 0,
        opacity: isLocked ? 0.7 : 1, // leve feedback visual
      }}
    >
      <div style={{ marginBottom: "7px", marginTop: "20px" }}>
        <label
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            fontWeight: 300,
            color: error ? "#D32F2F" : "#686D79FF",
          }}
        >
          {label}
        </label>
      </div>

      <div
        ref={wrapperRef}
        style={inputContainerStyle}
        aria-disabled={isLocked}
        onClick={() => {
          if (!isLocked) setOpen((v) => !v);
        }}
      >
        <div
          style={{
            padding: "0 24px 0 8px",
            lineHeight: "40px",
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            color: isLocked ? "#666" : current ? "#000" : "#999",
            userSelect: "none",
            textTransform: uppercase ? "uppercase" : "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {current ? isUpper(current.label) : placeholder}
        </div>

        {/* seta no canto direito (some quando bloqueado) */}
        {!isLocked && (
          <div
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: "10px",
              color: "#686D79",
            }}
          >
            ▼
          </div>
        )}

        {/* dropdown */}
        {open && !isLocked && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "0 0 2px 2px",
              maxHeight: "150px",
              overflowY: "auto",
              zIndex: 10,
            }}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                tabIndex={opt.disabled ? -1 : 0}
                onMouseDown={(e) => {
                  // Seleciona e fecha ANTES do onClick/document mousedown
                  if (opt.disabled) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(opt.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (opt.disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setOpen(false);
                  }
                }}
                style={{
                  padding: "8px",
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "14px",
                  backgroundColor: opt.value === value ? "#f0f0f0" : "#fff",
                  color: opt.disabled ? "#aaa" : "#000",
                  textTransform: uppercase ? "uppercase" : "none",
                }}
              >
                {isUpper(opt.label)}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: 6,
            color: "#D32F2F",
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default DropField;
